import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, setDoc, serverTimestamp, orderBy, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import './RoomsPage.css';
import LoadingSpinner from '../components/LoadingSpinner';
import EditableContentMultilang from '../components/EditableContentMultilang';
import { useTranslation } from 'react-i18next';

// --- Componente per Input Multilingua ---
const MultilangInput = ({ label, field, values, onChange, isTextarea = false, rows = 3 }) => {
    const languages = ['it', 'en', 'de', 'nl', 'fr'];
    return (
        <div className="form-group">
            <label>{label}</label>
            <div className="multilang-input-grid">
                {languages.map(lang => ( isTextarea ? ( <textarea key={lang} value={values[lang] || ''} onChange={(e) => onChange(field, lang, e.target.value)} placeholder={lang.toUpperCase()} rows={rows} className="multilang-input"/> ) : ( <input key={lang} type="text" value={values[lang] || ''} onChange={(e) => onChange(field, lang, e.target.value)} placeholder={lang.toUpperCase()} className="multilang-input"/> ) ))}
            </div>
        </div>
    );
};

// --- Componente per il selettore di immagini ---
const ImageSelectorModal = ({ uploadedImages, onSelect, onCancel }) => {
    return (
        <div className="image-selector-modal-overlay" onClick={onCancel}>
            <div className="image-selector-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Scegli un'immagine caricata</h3>
                {uploadedImages.length === 0 ? (
                    <p>Nessuna immagine pre-caricata. Caricane una nella sezione "Galleria Immagini" in fondo al form.</p>
                ) : (
                    <div className="image-selector-grid">
                        {uploadedImages.map((url, index) => (
                            <div key={index} className="image-selector-item" onClick={() => onSelect(url)}>
                                <img src={url} alt={`Immagine caricata ${index + 1}`} />
                            </div>
                        ))}
                    </div>
                )}
                <button type="button" className="cancel-button" onClick={onCancel}>Chiudi</button>
            </div>
        </div>
    );
};

// --- Componente per gestire un singolo blocco di contenuto ---
const ContentBlockEditor = ({ block, index, onUpdate, onRemove, onMove, onOpenImageSelector }) => {
    const renderSizeSelector = () => {
        const options = block.type === 'text' ? [ { value: 'normal', label: 'Normale' }, { value: 'large', label: 'Grande' }, { value: 'medium', label: 'Medio' }, { value: 'small', label: 'Piccolo' } ] : [ { value: 'full', label: 'Piena Larghezza' }, { value: 'large', label: 'Grande (75%)' }, { value: 'medium', label: 'Media (50%)' }, { value: 'small', label: 'Piccola (30%)' } ];
        return ( <select className="block-size-selector" value={block.size || (block.type === 'text' ? 'normal' : 'full')} onChange={(e) => onUpdate(index, 'size', e.target.value)} title="Dimensione Blocco">{options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select> );
    };
    const handleTextBlockChange = (field, lang, value) => { const newTextValue = { ...(block.value || {}), [lang]: value }; onUpdate(index, 'value', newTextValue); };
    if (block.type === 'text') { return ( <div className="content-block-editor"><div className="block-controls"><span>Blocco Testo</span>{renderSizeSelector()}<div className="block-actions"><button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}>▲</button><button type="button" onClick={() => onMove(index, 1)}>▼</button><button type="button" onClick={() => onRemove(index)} className="delete-block-btn">🗑️</button></div></div><MultilangInput label="" field="value" values={block.value || {}} onChange={handleTextBlockChange} isTextarea rows={6} /></div> ); }
    if (block.type === 'image') { return ( <div className="content-block-editor"><div className="block-controls"><span>Blocco Immagine</span>{renderSizeSelector()}</div><div className="image-block-input-group"><input type="text" value={block.value} onChange={(e) => onUpdate(index, 'value', e.target.value)} placeholder="URL immagine (incolla o scegli)" className="image-url-input"/><button type="button" onClick={() => onOpenImageSelector(index)} className="choose-image-btn">Scegli</button></div>{block.value && <img src={block.value} alt="anteprima" className="block-image-preview"/>}<div className="block-actions-bottom"><button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}>▲</button><button type="button" onClick={() => onMove(index, 1)}>▼</button><button type="button" onClick={() => onRemove(index)} className="delete-block-btn">🗑️</button></div></div> ); }
    return null;
};


// --- Componente Form Aggiornato con Galleria Immagini ---
const RoomForm = ({ initialData = {}, onSubmit, onCancel, isSaving }) => {
    const [name, setName] = useState(initialData.name || {});
    const [shortDescription, setShortDescription] = useState(initialData.shortDescription || {});
    const [imageUrl, setImageUrl] = useState(initialData.imageUrl || '');
    const [price, setPrice] = useState(initialData.price || '');
    const [detailContent, setDetailContent] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [targetBlockIndex, setTargetBlockIndex] = useState(null);

    useEffect(() => {
        setName(initialData.name || {});
        setShortDescription(initialData.shortDescription || {});
        setImageUrl(initialData.imageUrl || '');
        setPrice(initialData.price || '');
        const contentWithIds = (initialData.detailContent || []).map(b => ({...b, _id: Math.random().toString(36).substr(2, 9)}));
        setDetailContent(contentWithIds);
        setUploadedImages([]);
    }, [initialData]);
    
    const handleTextChange = (field, lang, value) => { const setter = { name: setName, shortDescription: setShortDescription }[field]; if (setter) { setter(prev => ({ ...prev, [lang]: value })); } };
    const handleSubmit = (e) => { e.preventDefault(); const contentToSave = detailContent.map(({ _id, ...rest }) => rest); onSubmit({ name, shortDescription, imageUrl, price: price.trim(), detailContent: contentToSave }); };
    const addContentBlock = (type) => setDetailContent([...detailContent, { type, value: type === 'text' ? {} : '', size: type === 'text' ? 'normal' : 'full', _id: Math.random().toString(36).substr(2, 9)}]);
    const updateContentBlock = (index, field, value) => { const nc = [...detailContent]; nc[index] = { ...nc[index], [field]: value }; setDetailContent(nc); };
    const removeContentBlock = (index) => { if (window.confirm("Rimuovere blocco?")) setDetailContent(detailContent.filter((_, i) => i !== index)); };
    const moveContentBlock = (index, dir) => { const ni = index + dir; if (ni < 0 || ni >= detailContent.length) return; const nc = [...detailContent]; [nc[index], nc[ni]] = [nc[ni], nc[index]]; setDetailContent(nc); };
    const openImageSelector = (index) => { setTargetBlockIndex(index); setIsSelectorOpen(true); };
    const handleImageSelect = (url) => { if (targetBlockIndex !== null) { updateContentBlock(targetBlockIndex, 'value', url); } setIsSelectorOpen(false); setTargetBlockIndex(null); };

    return (
        <>
            {isSelectorOpen && (<ImageSelectorModal uploadedImages={uploadedImages} onSelect={handleImageSelect} onCancel={() => setIsSelectorOpen(false)}/>)}
            <div className="room-form-modal">
                <form onSubmit={handleSubmit}>
                    <h4>{initialData.id ? 'Modifica Dettagli Camera' : 'Aggiungi Nuova Camera'}</h4>
                    <h5>Informazioni Principali</h5>
                    <MultilangInput label="Nome Camera*" field="name" values={name} onChange={handleTextChange} />
                    <MultilangInput label="Descrizione Breve*" field="shortDescription" values={shortDescription} onChange={handleTextChange} isTextarea />
                    <div className="form-group"><label>Immagine Principale*</label><ImageUploader currentImageUrl={imageUrl} onUploadComplete={setImageUrl} folderPath="rooms/main" /></div>
                    <hr className="form-divider" />
                    <h5>Contenuti Pagina Dettaglio</h5>
                    <p className="form-instructions">Aggiungi blocchi e assegna le immagini caricate nella galleria qui sotto.</p>
                    <div className="content-blocks-container">
                        {detailContent.map((block, index) => (
                            <ContentBlockEditor key={block._id} index={index} block={block} onUpdate={updateContentBlock} onRemove={removeContentBlock} onMove={moveContentBlock} onOpenImageSelector={openImageSelector}/>
                        ))}
                    </div>
                    <div className="add-block-buttons">
                        <button type="button" onClick={() => addContentBlock('text')}>+ Aggiungi Testo</button>
                        <button type="button" onClick={() => addContentBlock('image')}>+ Aggiungi Immagine (Vuoto)</button>
                    </div>
                    <hr className="form-divider" />
                    <h5>Galleria Immagini</h5>
                    <p className="form-instructions">Carica qui tutte le immagini... (Puoi selezionarne più di una tenendo premuto Ctrl o Cmd).</p>
                    <div className="temporary-image-gallery">
                        <ImageUploader 
                            currentImageUrl="" 
                            onUploadComplete={(url) => setUploadedImages(prev => [...prev, url])} 
                            folderPath="rooms/details_temp" 
                            multiple={true} /* <---- AGGIUNGI QUESTA PROPRIETÀ ----> */
                        />
                        <div className="uploaded-previews">
                            {uploadedImages.map((url, i) => <img key={i} src={url} alt={`caricata ${i+1}`} />)}
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="cta-button" disabled={isSaving || !imageUrl}>Salva Camera</button>
                        <button type="button" className="cancel-button" onClick={onCancel} disabled={isSaving}>Annulla</button>
                    </div>
                </form>
            </div>
        </>
    );
};

// --- Componente Card Camera con Traduzione ---
const RoomCard = ({ room, onEdit, onDelete }) => {
    const { i18n } = useTranslation();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const lang = i18n.language;
    const fallbackLang = 'it';
    const name = room.name?.[lang] || room.name?.[fallbackLang] || 'Nome non disponibile';
    const description = room.shortDescription?.[lang] || room.shortDescription?.[fallbackLang] || 'Descrizione...';
    const { t } = useTranslation();
    
    return (
        <div className="card room-card-item">
            <img 
                src={room.imageUrl || '/placeholder-image.jpg'} 
                alt={name} 
                className="card-img" 
                onError={(e) => { e.target.onerror = null; e.target.src='/placeholder-image.jpg'}}
            />
            <div className="card-body">
                <h3>{name}</h3>
                <p>{description}</p>
                <div className="card-buttons">
                    <Link to={`/camere/${room.id}`} className="cta-button details-button">
                        {t('buttonSeeDetails')}
                    </Link>
                </div>
            </div>
            {currentUser && (
                <div className="admin-card-controls">
                    <button onClick={() => onEdit(room)} title="Modifica Dettagli">✏️</button>
                    <button onClick={() => onDelete(room.id, name)} title="Elimina Camera">🗑️</button>
                </div>
            )}
        </div>
    );
};

// --- Componente Pagina Principale ---
const RoomsPage = () => {
    const [rooms, setRooms] = useState([]);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setError(null);
        const roomsQuery = query(collection(db, 'rooms'), orderBy('name.it'));
        const unsubscribe = onSnapshot(roomsQuery, (querySnapshot) => {
            setRooms(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            setError("Impossibile caricare le camere.");
        });
        return () => unsubscribe();
    }, []);

    const handleAddClick = () => { setEditingRoom(null); setShowForm(true); setError(null); };
    const handleEditClick = (room) => { setEditingRoom(room); setShowForm(true); setError(null); };
    const handleFormCancel = () => { setShowForm(false); setEditingRoom(null); setError(null); };
    const handleDeleteClick = async (id, name) => { if (window.confirm(`Eliminare "${name}"?`)) { try { await deleteDoc(doc(db, 'rooms', id)); alert(`Camera "${name}" eliminata.`); } catch (err) { alert("Errore eliminazione."); } } };
    const handleFormSubmit = async (formData) => {
        setIsSaving(true);
        setError(null);
        const dataToSave = { ...formData, lastUpdatedBy: currentUser?.email, updatedAt: serverTimestamp() };
        try {
            if (editingRoom?.id) {
                await setDoc(doc(db, 'rooms', editingRoom.id), dataToSave, { merge: true });
            } else {
                dataToSave.createdAt = serverTimestamp();
                await addDoc(collection(db, 'rooms'), dataToSave);
            }
            setShowForm(false);
            setEditingRoom(null);
        } catch (err) {
            setError("Errore durante il salvataggio. Riprova.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
                    <div className="rooms-page">
                <LoadingSpinner show={isSaving} />
                <section className="page-header">
                    <div className="container text-center">
                        <EditableContentMultilang docPath="rooms_page/header" field="title" elementType="h1" />
                        <EditableContentMultilang docPath="rooms_page/header" field="description" elementType="p" />
                        {currentUser && !showForm && (<button onClick={handleAddClick} className="cta-button add-room-button">+ Aggiungi Camera</button>)}
                    </div>
                </section>
                {showForm && currentUser && (
                    <section className="content-section room-form-section">
                        <div className="container">
                            <RoomForm initialData={editingRoom || {}} onSubmit={handleFormSubmit} onCancel={handleFormCancel} isSaving={isSaving}/>
                            {error && !isSaving && <p className="form-status error">{error}</p>}
                        </div>
                    </section>
                )}
                <section className="content-section">
                    <div className="container">
                        {error && !showForm && <p className="form-status error">{error}</p>}
                        {!error && rooms.length === 0 && (<p className="no-rooms-text">Nessuna camera trovata.</p>)}
                        {rooms.length > 0 && (
                            <div className="rooms-list-grid">
                                {rooms.map(room => (
                                    <RoomCard key={room.id} room={room} onEdit={handleEditClick} onDelete={handleDeleteClick}/>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
    );
};

export default RoomsPage;

